'use no memo';

import { createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useMemo } from 'react';
import { Card } from '@/components/Card/card';
import { DataTable, DataTablePagination, useTableDisplayState } from '@/components/DataTable';
import Icon, { IconSize } from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import SearchInput from '@/components/SearchInput';
import { Text } from '@/components/Typography';
import { isForbiddenError } from '@/lib/api/is-forbidden-error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useFreeTextSearch, usePaginationSearch } from '@/lib/listPage';
import { ListFilterTabs, type ListFilterTabItem } from '../-components/ListFilterTabs';
import { ListForbiddenState } from '../-components/ListForbiddenState';
import { selectVisibleLedgerScopes, useLedgerDataScopes } from '../-query/useLedgerDataScopes';
import { useAccountColumns } from './-columns';
import { useAccountLedgerQuery } from './-query';
import { SearchSchema } from './-zod-schema';
type AccountScope = 'self';
export const Route = createFileRoute('/_individual/individual/accounts/')({
  validateSearch: zodValidator(SearchSchema),
  component: AccountsPage
});
function AccountsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.accounts'
  });
  const {
    t: tc
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.common'
  });
  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: next => navigate({
      search: () => next
    })
  });
  const {
    searchText,
    debouncedSearchText,
    handleSearchTextChange
  } = useFreeTextSearch({
    pagination,
    setPagination
  });

  // 使用可能なデータスコープはセッションに畳み込まれている（DC-8324）。
  // account（アカウント）は self のみ（managed-place・all 非対応）。
  const dataScopes = useLedgerDataScopes();
  const visibleScopes = useMemo(() => selectVisibleLedgerScopes(dataScopes, 'account').filter((scope): scope is AccountScope => scope !== 'managed-place'), [dataScopes]);

  // 表示可能な scope でなければ先頭の可視 scope にフォールバックする。
  const effectiveScope: AccountScope = visibleScopes.includes(search.scope) ? search.scope : visibleScopes[0] ?? search.scope;

  // URL の scope が可視 scope と食い違う場合は正規化する。
  useEffect(() => {
    if (visibleScopes.length > 0 && search.scope !== effectiveScope) {
      void navigate({
        search: prev => ({
          ...prev,
          scope: effectiveScope,
          pageIndex: 0
        }),
        replace: true
      });
    }
  }, [visibleScopes.length, search.scope, effectiveScope, navigate]);

  // 閲覧拒否と「権限あり・データ範囲 0 件」は FE のセッション情報だけでは区別できない
  // （どちらも dataScopes から消える）。BE の 403（拒否）/ 200 空（0 件）で出し分ける
  // ため、クエリは常に実行する（DC-8903）。
  const {
    data,
    isPending,
    isError,
    error
  } = useAccountLedgerQuery({
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    scope: effectiveScope,
    freeText: debouncedSearchText === '' ? undefined : debouncedSearchText
  });
  const columns = useAccountColumns();
  const rows = data?.items ?? [];

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- Compiler非互換のサードパーティAPI
  const table = useReactTable({
    data: rows,
    columns,
    pageCount: data?.maxPage ?? -1,
    state: {
      pagination
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true
  });
  const displayState = useTableDisplayState(isPending, rows);
  const scopeLabels: Record<AccountScope, string> = {
    self: t('tabs.self')
  };
  const tabs: ListFilterTabItem<AccountScope>[] = visibleScopes.map(scope => ({
    value: scope,
    label: scopeLabels[scope]
  }));
  // granted な scope が 1 つでもあればタブを表示する（単一 scope でも表示）。
  const showScopeTabs = tabs.length > 0;

  // 閲覧拒否（BE が 403）は権限エラー表示に倒す。権限あり・データ 0 件は BE が 200 空を
  // 返すため空テーブル（該当なし）になる。FE セッションだけでは両者を区別できないため
  // BE の応答を正とする（DC-8886 / DC-8903）。
  if (isError && isForbiddenError(error)) {
    return <div className="flex h-full min-w-0 flex-col overflow-hidden">
        <PageHeader title={t('pageTitle')} />
        <VStack className="min-h-0 flex-1 gap-4 px-5 py-6 md:px-12 md:py-8">
          <ListForbiddenState onBack={() => void navigate({
          to: '/individual'
        })} />
        </VStack>
      </div>;
  }
  if (isError) {
    throw error;
  }
  return <div className="flex h-full min-w-0 flex-col overflow-hidden">
      <PageHeader title={t('pageTitle')} />
      <VStack className="min-h-0 flex-1 gap-4 px-5 py-6 md:px-12 md:py-8">
        <HStack className="items-center gap-2">
          {showScopeTabs && <ListFilterTabs value={effectiveScope} tabs={tabs} onChange={scope => navigate({
          search: prev => ({
            ...prev,
            scope,
            pageIndex: 0
          })
        })} />}
          <div className="w-full max-w-xs">
            <SearchInput value={searchText} onChange={e => handleSearchTextChange(e.target.value)} placeholder={tc('search')} />
          </div>
        </HStack>
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <DataTable table={table} displayState={displayState} onRowClick={null} />
        </Card>
        <DataTablePagination table={table} totalCount={data?.count} pageOptions={[20, 50, 100]} />
      </VStack>
    </div>;
}
function PageHeader({
  title
}: {
  title: string;
}) {
  return <div className="flex shrink-0 items-center gap-3 border-b border-neutral-muted-border bg-neutral-fill px-5 py-3 shadow-sm md:px-12 md:py-4">
      <span className="text-neutral-on-fill">
        <Icon iconName="KeyRoundIcon" size={IconSize.XL} inheritColor />
      </span>
      <Text as="h1" variant="h3">
        {title}
      </Text>
    </div>;
}